import { syncDexieUpdatedBookings } from "@/db/updated-bookings";
import { isUserLoggedIn } from "@/utils/vue-helpers/user";
import { syncDexieBookings } from "@/db/untyped-booking";
import { syncDexiePosFiles } from "@/db/pos-files";
import { syncDexieReceiptCounters } from "@/db/receipt-counter";
import { syncDexieTransactions } from "@/db/transaction";
import { dexieDB } from "./dexie-database";
import { SyncStatus } from "@/types/db/product-slot-booking";
import BookingModule from "@/store/modules/booking";
import { getAccountingWithUUID } from "@/api/gql/product-slot-boooking";
import { AccountingState } from "@/types/db/accounting";
import { LogService } from "./logging";
import { cloneDeep } from "lodash";
import NotificationService from "./notification.service";
import i18n from "@/lang";
// Simplified type alias to avoid TS2589 (excessively deep type instantiation)
// caused by vue-i18n v11's complex overloaded generics on t()
const t: (key: string, ...args: unknown[]) => string = (i18n.global as any).t;

// Lock keys — use localStorage to share locks across app windows (main + customer display)
const ORDERS_LOCK_KEY = "__SMEETZ_IS_SYNCING_ORDERS__";
const POS_FILES_LOCK_KEY = "__SMEETZ_IS_SYNCING_POS_FILES__";
const RECEIPT_COUNTERS_LOCK_KEY = "__SMEETZ_IS_SYNCING_RECEIPT_COUNTERS__";
const TRANSACTIONS_LOCK_KEY = "__SMEETZ_IS_SYNCING_TRANSACTIONS__";

const SYNC_SERVICE_INSTANCE_ID = Math.random().toString(36).slice(2);

export const BOOKING_SYNC_BATCH_LIMIT = 3;
export const MAX_SYNC_ATTEMPTS = 10;

function getGlobalLock(key: string): boolean {
  return localStorage.getItem(key) === "true";
}

function setGlobalLock(key: string, v: boolean) {
  if (v) {
    localStorage.setItem(key, "true");
  } else {
    localStorage.removeItem(key);
  }
}

/**
 * Reset all sync locks. Call this on app startup to clear any stale locks.
 */
export function resetAllSyncLocks() {
  setGlobalLock(ORDERS_LOCK_KEY, false);
  setGlobalLock(POS_FILES_LOCK_KEY, false);
  setGlobalLock(RECEIPT_COUNTERS_LOCK_KEY, false);
  setGlobalLock(TRANSACTIONS_LOCK_KEY, false);
}

/**
 * Function to initiate the sync job for orders (untyped and updated bookings)
 */
export async function initiateOrdersSync(): Promise<void> {
  LogService.info(
    `Sync: initiateOrdersSync() instance=${SYNC_SERVICE_INSTANCE_ID}`
  );
  if (!navigator.onLine || !isUserLoggedIn.value) {
    LogService.info(
      "Sync: Aborted sync job due to offline or user not logged in",
      {
        online: navigator.onLine,
        userLoggedIn: isUserLoggedIn.value,
      }
    );
    return;
  }

  if (getGlobalLock(ORDERS_LOCK_KEY)) {
    LogService.info(`Sync: Aborted sync job because lock is active`, {
      instanceId: SYNC_SERVICE_INSTANCE_ID,
    });
    return;
  }

  setGlobalLock(ORDERS_LOCK_KEY, true);

  LogService.info("Sync: Initiating orders sync");
  // 1. Sync untyped bookings
  await syncDexieBookings(BOOKING_SYNC_BATCH_LIMIT);
  // 2. Sync updated bookings
  await syncDexieUpdatedBookings(BOOKING_SYNC_BATCH_LIMIT);
  LogService.info("Sync: Completed orders sync");
  setGlobalLock(ORDERS_LOCK_KEY, false);

  const totalUnsynced = await unsyncedBookingsCount();

  // If there are more unsynced (Pending or Failed) orders, re-trigger the sync job right away
  if (totalUnsynced > 0) {
    setTimeout(initiateOrdersSync, 1000);
  }
}

/**
 * Initiate sync for POS files (custom field file uploads)
 */
export async function initiatePosFilesSync(): Promise<void> {
  if (!navigator.onLine || !isUserLoggedIn.value) return;
  if (getGlobalLock(POS_FILES_LOCK_KEY)) return;

  setGlobalLock(POS_FILES_LOCK_KEY, true);
  try {
    await syncDexiePosFiles();
  } catch (error) {
    LogService.info("Sync: Error during POS files sync", { error });
  } finally {
    setGlobalLock(POS_FILES_LOCK_KEY, false);
  }
}

/**
 * Initiate sync for receipt counter payloads (failed PATCH retries)
 */
export async function initiateReceiptCountersSync(): Promise<void> {
  if (!navigator.onLine || !isUserLoggedIn.value) return;
  if (getGlobalLock(RECEIPT_COUNTERS_LOCK_KEY)) return;

  setGlobalLock(RECEIPT_COUNTERS_LOCK_KEY, true);
  try {
    await syncDexieReceiptCounters();
  } catch (error) {
    LogService.info("Sync: Error during receipt counters sync", { error });
  } finally {
    setGlobalLock(RECEIPT_COUNTERS_LOCK_KEY, false);
  }
}

/**
 * Initiate sync for Fiskaly transactions
 */
export async function initiateTransactionsSync(): Promise<void> {
  if (!navigator.onLine || !isUserLoggedIn.value) return;
  if (getGlobalLock(TRANSACTIONS_LOCK_KEY)) return;

  setGlobalLock(TRANSACTIONS_LOCK_KEY, true);
  try {
    await syncDexieTransactions();
  } catch (error) {
    LogService.info("Sync: Error during transactions sync", { error });
  } finally {
    setGlobalLock(TRANSACTIONS_LOCK_KEY, false);
  }

  // Check if there are remaining unsynced transactions and re-trigger
  try {
    const remaining = await dexieDB.untypedTransactions
      .where("syncStatus")
      .anyOf([SyncStatus.Pending, SyncStatus.Failed])
      .count();
    if (remaining > 0) {
      setTimeout(initiateTransactionsSync, 1000);
    }
  } catch {
    // ignore
  }
}

export async function unsyncedBookingsCount(): Promise<number> {
  const [
    pendingUntypedCount,
    failedUntypedCount,
    pendingUpdatedCount,
    failedUpdatedCount,
  ] = await Promise.all([
    // Untyped Bookings
    dexieDB.untypedBookings
      .where("syncStatus")
      .equals(SyncStatus.Pending)
      .count(),
    dexieDB.untypedBookings
      .where("[syncStatus+attempts]")
      .between([SyncStatus.Failed, 0], [SyncStatus.Failed, MAX_SYNC_ATTEMPTS])
      .count(),
    // Updated Bookings
    dexieDB.updatedBookings
      .where("syncStatus")
      .equals(SyncStatus.Pending)
      .count(),
    dexieDB.updatedBookings
      .where("[syncStatus+attempts]")
      .between([SyncStatus.Failed, 0], [SyncStatus.Failed, MAX_SYNC_ATTEMPTS])
      .count(),
  ]);

  return (
    pendingUntypedCount +
    failedUntypedCount +
    pendingUpdatedCount +
    failedUpdatedCount
  );
}

/*
Periodically checks for Adyen refund accounting state, notifies the user when the refund is completed or failed.
*/
export async function startRefundListener(
  bookingUuid: string,
  accountingUuid: string
) {
  LogService.info(
    "Starting refund listener job for accounting:",
    accountingUuid
  );
  BookingModule.addRefundListener({
    bookingUuid: bookingUuid,
    accountingUuid: accountingUuid,
  });
  const accounting = await getAccountingWithUUID(accountingUuid);
  if (accounting && accounting.state !== AccountingState.Started) {
    LogService.info(
      `Accounting ${accountingUuid} state changed to ${accounting.state}`
    );
    BookingModule.removeRefundListener({
      bookingUuid: bookingUuid,
      accountingUuid: accountingUuid,
    });
    const booking = BookingModule.bookings[bookingUuid];
    if (!booking) {
      LogService.info("Booking not found for UUID:", bookingUuid);
      return;
    }
    const bookingCopy = cloneDeep(booking);
    const refundAccountingIndex = bookingCopy.accountings.findIndex(
      (acc) => acc.uuid === accountingUuid
    );
    if (refundAccountingIndex === -1) {
      LogService.info("Refund accounting not found in booking:", bookingUuid);
      return;
    }
    bookingCopy.accountings[refundAccountingIndex].state = accounting.state;
    BookingModule.updateBookingAccountings({
      bookingUUID: bookingUuid,
      accountings: bookingCopy.accountings,
    });

    if (accounting.state === AccountingState.Completed) {
      NotificationService.showCustomMessageWithTitle(
        t("adyen-refund.refund-success-message").replace(
          "XXXX",
          bookingCopy.reference
        ),
        t("adyen-refund.refund-success"),
        "success"
      );
    } else {
      NotificationService.showCustomMessageWithTitle(
        `Your refund for the order ${bookingCopy.reference} has failed.`,
        `Refund Failed`,
        "error"
      );
    }
  } else {
    LogService.info(
      `Refund accounting ${accountingUuid} is still in Started state, retrying in 5 seconds ...`
    );
    // Retry after 5 seconds
    setTimeout(() => {
      startRefundListener(bookingUuid, accountingUuid);
    }, 5000);
  }
}
